import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetTestimonials } from "../../hooks/testimonials/useGetTestimonials";
import { useDeleteTestimonial } from "../../hooks/testimonials/useDeleteTestimonial";
import { useDeleteAllTestimonials } from "../../hooks/testimonials/useDeleteAllTestimonials";
import { useRestoreAllTestimonials } from "../../hooks/testimonials/useRestoreAllTestimonials";
import { useForceDeleteAllTestimonials } from "../../hooks/testimonials/useForceDeleteAllTestimonials";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import TestimonialsTableRow from "./Table/TestimonialsTableRow";
import { TestimonialsTableColumns } from "./Table/TestimonialsTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const TestimonialsTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-testimonial"),
            create: has("create-testimonial"),
            edit: has("update-testimonial"),
            delete: has("delete-testimonial"),
        }),
        [has]
    );

    const navigate = useNavigate();

    const [trashedFilter, setTrashedFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    const { deleteItemMutate, isPending: isDelete } = useDeleteTestimonial();
    const { deleteAllItemsMutate, isPending: isDeleteAll } = useDeleteAllTestimonials();
    const { restoreAllItemsMutate, isPending: isRestoreAll } = useRestoreAllTestimonials();
    const { forceDeleteAllItemsMutate, isPending: isForceDeleteAll } = useForceDeleteAllTestimonials();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/testimonials",
        deleteSingle: deleteItemMutate,
        deleteAll: deleteAllItemsMutate,
        restoreSingle: (id, options) => restoreAllItemsMutate({ items: [id] }, options),
        restoreAll: restoreAllItemsMutate,
        forceDeleteSingle: (id, options) => forceDeleteAllItemsMutate({ items: [id] }, options),
        forceDeleteAll: forceDeleteAllItemsMutate,
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            is_active: statusFilter || undefined,
            sort_by: 'sort_order',
            sort_order: order,
        }),
        [page, perPage, search, trashedFilter, statusFilter, order]
    );

    const { isLoading, error, items, meta } = useGetTestimonials(params);

    const filters = useMemo(() => [
        {
            key: "is_active",
            label: { ar: "الحالة", en: "Status" },
            value: statusFilter,
            onChange: setStatusFilter,
            options: [
                { value: "", label: { ar: "الكل", en: "All" } },
                { value: "1", label: { ar: "نشط", en: "Active" } },
                { value: "0", label: { ar: "غير نشط", en: "Inactive" } },
            ],
        },
    ], [statusFilter]);

    const columns = useMemo(() => TestimonialsTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "الشهادات", en: "Testimonials" }}
            columns={columns}
            items={items}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, items)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/testimonials/create")}
            addButtonLabel={{ ar: "إضافة شهادة", en: "Add Testimonial" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <TestimonialsTableRow
                    {...params}
                    key={params.item.id}
                    item={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default TestimonialsTable;
